import type { AxiosInstance } from "axios";
import type { IFactoryFunctionRequest, IProject, paginate } from "@bc-library/interface";
import { EMethod } from "@bc-library/interface";
import { newDateFromUTC, newDateToUTC } from "@bc-library/common";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";
import useAxios from "@/services/api/useAxios";
import {
  afterReceiving,
  afterReceivingOne,
  afterReceivingChart,
  beforeSending,
  beforeSendingList,
} from "@/services/project/hooks";
import { afterReceiving as afterReceivingEvaluation } from "@/services/evaluation/hooks";

const resource = "engagements";

const showFirmPaginated = (axios: IFactoryFunctionRequest<any, any>) => (query: any) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
    afterReceivingHook: afterReceiving,
  });
};

const showFunnelFirmPaginated = (axios: IFactoryFunctionRequest<any, any>) => (query: any) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/funnel`,
    query,
    afterReceivingHook: afterReceiving,
  });
};

const afterReceivingLeadStatus = (data: any) => {
  if (!data || typeof data !== "object") return data;
  if (data.engagement) {
    return {
      ...data,
      engagement: afterReceivingOne(data.engagement),
    };
  }
  return afterReceivingOne(data);
};

const afterReceivingCreateDeal = (data: any) => {
  if (!data || typeof data !== "object") return data;
  if (data.deal) {
    return {
      ...data,
      deal: afterReceivingOne(data.deal),
    };
  }
  return afterReceivingOne(data);
};

const patchLeadStatus =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (id: string, payload: { currentStatus: number; source?: string }) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/funnel/${id}/lead-status`,
      data: payload,
      afterReceivingHook: afterReceivingLeadStatus,
    });
  };

const createFunnelDeal =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (payload: {
    clientId: string;
    display: string;
    currentStatus?: number;
    directorId?: string;
    leadPrice?: number;
    leadProbability?: number;
    integrationExclusions?: string[];
  }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/funnel/deals`,
      data: payload,
      afterReceivingHook: afterReceivingCreateDeal,
    });
  };

const bulkLeadStatus =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (payload: { ids: string[]; currentStatus: number; source?: string }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/funnel/bulk-lead-status`,
      data: payload,
      afterReceivingHook: afterReceiving,
    });
  };

const getFunnelInsights =
  (axios: IFactoryFunctionRequest<any, any>) => (query?: Record<string, unknown>) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/funnel/insights`,
      query,
    });
  };

const afterReceivingDealWorkspace = (data: any) => {
  if (!data || typeof data !== "object") return data;
  return {
    ...data,
    engagement: data.engagement ? afterReceivingOne(data.engagement) : data.engagement,
  };
};

const getDealWorkspace = (axios: IFactoryFunctionRequest<any, any>) => (engagementId: string) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/funnel/${engagementId}/deal`,
    afterReceivingHook: afterReceivingDealWorkspace,
  });
};

const patchFunnelCrm =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (engagementId: string, data: Record<string, unknown>) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/funnel/${engagementId}/crm`,
      data,
      beforeSendingHook: beforeSending,
      afterReceivingHook: afterReceivingOne,
    });
  };

const showAdvancement =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    page,
    limit,
    searchString,
    currentScope,
  }: {
    page: number;
    limit: number;
    searchString: string;
    firmId: string;
    currentScope?: string | boolean;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/advancement`,
      query: {
        display: searchString,
        page,
        limit,
        isArchived: currentScope,
      },
      afterReceivingHook: afterReceiving,
    });
  };
const showProjectTimesheet =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    page,
    limit,
    display,
    projectIds = [],
    currentStatus,
    resourceId,
  }: {
    page: number;
    limit: number;
    display: string;
    projectIds: string[];
    currentStatus?: number;
    resourceId?: string;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/picker`,
      query: {
        display,
        page,
        limit,
        projectIds,
        currentStatus,
        resourceId,
      },
      afterReceivingHook: afterReceiving,
    });
  };
const getEvaluationProjectCreate =
  (axios: IFactoryFunctionRequest<paginate<any>, any>) =>
  (query: {
    page: number;
    limit: number;
    display: string;
    projectIds: string[];
    currentStatus?: number;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/evaluation/create`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };
const archiveProjects =
  (axios: IFactoryFunctionRequest<paginate<any>, any>) => (projectIds: string[]) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/archive`,
      data: { projectIds },
    });
  };

const getClientProjects =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    page,
    limit,
    display,
    clientIds = [],
    currentStatus,
    isArchived,
    withHealth,
    year,
  }: {
    page: number;
    limit: number;
    display: string;
    clientIds: string[];
    currentStatus?: number;
    isArchived?: boolean | "all" | string;
    withHealth?: boolean;
    year?: number;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/clientDetails`,
      query: {
        display,
        page,
        limit,
        clientIds,
        currentStatus,
        isArchived,
        withHealth: withHealth ? "true" : undefined,
        year,
      },
      afterReceivingHook: afterReceiving,
    });
  };
const getProjectInvoice =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ _id }: { _id: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/invoicing/${_id}`,
      afterReceivingHook: afterReceiving,
    });
  };
const getOne =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ _id }: { _id: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/${_id}`,
      afterReceivingHook: afterReceiving,
    });
  };

const getProjectInformation =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ _id }: { _id: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/information/${_id}`,
      afterReceivingHook: afterReceiving,
    });
  };

const checkIfProjectExist =
  (axios: IFactoryFunctionRequest<any, { project: undefined | { _id: string } }>) =>
  ({ projectCode }: { projectCode?: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/checkIfProjectExist`,
      query: { projectCode },
    });
  };
const getProjectTimesheet =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ _id }: { _id: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${_id}/timesheet`,
      afterReceivingHook: afterReceiving,
    });
  };
const getOneStaffing =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ _id }: { _id: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/showStaffing/${_id}`,
      afterReceivingHook: afterReceiving,
    });
  };

type TProjectBudget = Pick<
  IProject,
  | "endDate"
  | "startDate"
  | "display"
  | "clientId"
  | "isLocked"
  | "rebate"
  | "leadPrice"
  | "subcontractor"
> & {
  clientId: {
    _id: string;
    display: string;
  };
  staffingSetup: IProject["staffingSetup"] &
    {
      positionId: {
        _id: string;
        display: string;
        order?: number | null;
      };
    }[];
  contributors: {
    contributorId: {
      _id: string;
      display: string;
      firstname: string;
      lastname: string;
    };
    percentage: number;
  }[];
  conditionalFees: any[];
  invoicingInformation: {
    _id: string;
    display: string;
    currency: string;
  };
};
const getProjectBudget =
  (axios: IFactoryFunctionRequest<TProjectBudget, { _id: string }>) =>
  ({ _id }: { _id: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${_id}/budget`,
      afterReceivingHook: afterReceiving,
    });
  };

const getInterval =
  (axios: IFactoryFunctionRequest) =>
  ({ startDate, endDate, resources }: { startDate: Date; endDate: Date; resources: string[] }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/interval/${startDate}/${endDate}`,
      data: resources,
      afterReceivingHook: afterReceiving,
    });
  };

const getIntervalPlanningResource =
  (axios: IFactoryFunctionRequest) =>
  ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/interval/project/${startDate}/${endDate}`,
      data: {},
      afterReceivingHook: afterReceiving,
    });
  };

const updateOneStaffing =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    projectId,
    setupId,
    listId,
    staffingPeriod,
  }: {
    projectId: string;
    setupId: string;
    listId: string;
    staffingPeriod: any[];
  }) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/project/${projectId}/setup/${setupId}/list/${listId}`,
      data: { staffingPeriod },
      beforeSendingHook: beforeSendingList,
      afterReceivingHook: afterReceiving,
    });
  };

const updateProjectBasic =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId, data }: { projectId: string; data: any }) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/updateBasic/${projectId}`,
      data,
      beforeSendingHook: beforeSending,
      afterReceivingHook: afterReceiving,
    });
  };

const updateStreams =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId, streams }: { projectId: string; streams: any[] }) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/streams`,
      data: { streams },
      beforeSendingHook: beforeSending,
      afterReceivingHook: afterReceiving,
    });
  };

const updateResourceStaffingList =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    projectId,
    setupId,
    listId,
    resourceList,
  }: {
    projectId: string;
    setupId: string;
    listId: string;
    resourceList: { status: number; resourceId: string | any }[];
  }) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/project/${projectId}/setup/${setupId}/list/${listId}/resourcesList`,
      data: { resourceList },
      afterReceivingHook: afterReceiving,
    });
  };

const updateResourceStaffingPosition =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    projectId,
    setupId,
    positionId,
    listId,
  }: {
    projectId: string;
    setupId: string;
    positionId: string;
    listId: string;
  }) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/project/${projectId}/setup/${setupId}/position/${positionId}/list/${listId}`,
    });
  };

const deleteResourceStaffingList =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId, setupId, listId }: { projectId: string; setupId: string; listId: string }) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/project/${projectId}/setup/${setupId}/list/${listId}/deleteList`,
    });
  };
const createProject = (axios: IFactoryFunctionRequest<any, any>) => (data: any) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/createProject`,
    data,
    beforeSendingHook: beforeSending,
    afterReceivingHook: afterReceiving,
  });
};
const duplicateProject =
  (axios: IFactoryFunctionRequest<{ projectId: string }, { projectId: string }>) =>
  (_id: string, integrationExclusions?: string[]) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/duplicateProject/${_id}`,
      ...(integrationExclusions ? { query: { integrationExclusions: integrationExclusions.join(',') } } : {}),
    });
  };

const retreiveLastProjectCode = (axios: IFactoryFunctionRequest<any, any>) => () => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/retreiveLastProjectCode`,
  });
};

const updateOrCreateSetup =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId, positionId, setup }: { projectId: string; positionId: string; setup: any }) => {
    return axios({
      method: EMethod.put,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/setup/updateOrCreate/${projectId}/${positionId}`,
      data: setup,
      afterReceivingHook: (data) => {
        return {
          ...data,
          updatedProject: afterReceivingOne(data.updatedProject),
        };
      },
    });
  };

const getResourceStaffingBetweenDates =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ resourceId, startDate, endDate }: { resourceId: string; startDate: Date; endDate: Date }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/resource/${resourceId}/staffing/between/${startDate}/${endDate}`,
      afterReceivingHook: afterReceiving,
    });
  };

const showEngagementsList =
  (axios: IFactoryFunctionRequest<TShowInvoiceEngagement, undefined>) =>
  (query: { limit: number; page: number }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/invoicing`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };

export type TProjectToBeInvoiced = {
  _id: string;
  display: string;
  projectCode?: string;
  client: { _id: string; display: string } | null;
  startDate: Date;
  endDate: Date;
  isArchived: boolean;
  production: number;
  timesheetProduction: number;
  leadExpenses: number;
  invoiced: number;
  // Invoices behind the invoiced amount (number null when the invoice has none yet)
  invoices: { _id: string; number: string | null; isCreditNote: boolean; amount: number }[];
  toBeInvoiced: number;
  type: "FAE" | "PCA";
};

export type TProjectsToBeInvoiced = {
  cutoffDate: string;
  fiscalStartDate: string | null;
  totals: { fae: number; pca: number };
  projects: TProjectToBeInvoiced[];
};

const getProjectsToBeInvoiced =
  (axios: IFactoryFunctionRequest<TProjectsToBeInvoiced, undefined>) =>
  (query: { cutoffDate: string; fiscalStartDate?: string; minAmount?: number; type?: "FAE" | "PCA" }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/invoicing/toBeInvoiced`,
      query,
      afterReceivingHook: (data: TProjectsToBeInvoiced) => ({
        ...data,
        projects: data.projects.map((project) => ({
          ...project,
          startDate: newDateFromUTC(project.startDate),
          endDate: newDateFromUTC(project.endDate),
        })),
      }),
    });
  };

const getAvailableTimesheetBooking =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    year,
    month,
    resourceId,
    excludeProjectIds = [],
    immediate = true,
  }: {
    resourceId: string;
    year: number;
    month: number;
    excludeProjectIds: string[];
    immediate: boolean;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/availableTimesheetBooking/${year}/${month}/${resourceId}`,
      query: { excludeProjectIds },
      immediate,
      afterReceivingHook: afterReceiving,
    });
  };

// router.get('/firm/resource/confirmed/:id', auth.isAuthenticated(), controller.showResourceConfirmed);
const showResourceConfirmed =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    resourceId,
    page,
    limit,
    year,
  }: {
    resourceId: string;
    page: number;
    limit: number;
    year?: number;
  }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/resource/${resourceId}/confirmed`,
      query: { page, limit, ...(year && { year }) },
      afterReceivingHook: afterReceiving,
    });
  };

const getProjectCumulated =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId }: { projectId: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/monitoring/cumulated/${projectId}`,
      afterReceivingHook: (data) => {
        return {
          ...data,
          project: afterReceiving(data.project),
          production: afterReceivingChart(data.production),
        };
      },
    });
  };

const getProjectMarginMonitoring =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId }: { projectId: string }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/monitoring/margin/${projectId}`,
    });
  };
// const extractExpenseInformation = (axios: IFactoryFunctionRequest<any, any>) => ({ files }: { files: string[] }) => {
//   return axios({
//     method: EMethod.post,
//     url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/extractInformationsFromBill`,
//     data: { files },
//   })
// }

const oldFilter =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    page,
    search = "",
    startDate,
    endDate,
    limit,
  }: {
    page: number;
    search?: string;
    startDate?: Date;
    endDate?: Date;
    limit: number;
  }) => {
    const query = { page, search, limit };
    if (startDate) query.startDate = startDate.getTime();
    if (endDate) query.endDate = endDate.getTime();
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/filter`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };
const availableForExpense =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ page, search = "", limit }: { page: number; search?: string; limit: number }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/availableForExpense`,
      query: { page, search, limit },
      afterReceivingHook: afterReceiving,
    });
  };

const evaluationIntervalForResource =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ resourceId, startDate, endDate }) => {
    const utcStartDate = newDateToUTC(startDate).toISOString();
    const utcEndDate = newDateToUTC(endDate).toISOString();
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/interval/evaluation/${resourceId}/${utcStartDate}/${utcEndDate}`,
      afterReceivingHook: (data) => {
        return data.map((project) => {
          return {
            ...afterReceiving(project),
            evaluations: afterReceivingEvaluation(project.evaluations),
          };
        });
      },
    });
  };

const filter =
  (axios: IFactoryFunctionRequest<paginate<Array<Pick<IProject, "_id" | "display">>>, undefined>) =>
  (query = {}) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/filter`,
      query: query,
      afterReceivingHook: afterReceiving,
    });
  };

const exportProjectsToExcel = (axios: IFactoryFunctionRequest<any, any>) => (query) => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export`,
    query,
    immediate: false,
  });
};

const addSubcontractor =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId, subcontractor }: { projectId: string; subcontractor: any }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/addSubcontractor/${projectId}`,
      data: subcontractor,
      afterReceivingHook: afterReceiving,
    });
  };

const removeSubcontractor =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId, subcontractorId }: { projectId: string; subcontractorId: string }) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/removeSubcontractor/${projectId}/${subcontractorId}`,
      afterReceivingHook: afterReceiving,
    });
  };

const updateSubcontractor =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({
    projectId,
    subcontractorId,
    subcontractor,
  }: {
    projectId: string;
    subcontractorId: string;
    subcontractor: any;
  }) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/updateSubcontractor/${projectId}/${subcontractorId}`,
      data: subcontractor,
      afterReceivingHook: afterReceiving,
    });
  };

// Atomic budget update methods
const updateListItem =
  (axios: IFactoryFunctionRequest<{ success: boolean; modifiedCount: number }, any>) =>
  ({
    projectId,
    setupId,
    listItemId,
    changes,
  }: {
    projectId: string;
    setupId: string;
    listItemId: string;
    changes: Partial<{
      billedDays: number;
      realDays: number;
      dailyCost: number;
      dailyPrice: number;
      listPrice: number;
      productionPrice: number;
    }>;
  }) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/staffing-setup/${setupId}/list/${listItemId}`,
      data: changes,
      beforeSendingHook: beforeSendingList,
    });
  };

const updateStaffingPeriod =
  (axios: IFactoryFunctionRequest<{ success: boolean; modifiedCount: number }, any>) =>
  ({
    projectId,
    setupId,
    listItemId,
    staffingPeriod,
  }: {
    projectId: string;
    setupId: string;
    listItemId: string;
    staffingPeriod: Array<{ startDate: Date; endDate: Date; days: number }>;
  }) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/staffing-setup/${setupId}/list/${listItemId}/staffing-period`,
      data: { staffingPeriod },
      beforeSendingHook: beforeSendingList,
    });
  };

const updateSubcontractorAtomic =
  (axios: IFactoryFunctionRequest<{ success: boolean; modifiedCount: number }, any>) =>
  ({
    projectId,
    subcontractorId,
    changes,
  }: {
    projectId: string;
    subcontractorId: string;
    changes: Partial<{
      billedDays: number;
      realDays: number;
      dailyCost: number;
      dailyPrice: number;
      listPrice: number;
    }>;
  }) => {
    return axios({
      method: EMethod.patch,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/subcontractor/${subcontractorId}`,
      data: changes,
    });
  };

const deleteListItem =
  (axios: IFactoryFunctionRequest<{ success: boolean; modifiedCount: number }, any>) =>
  ({
    projectId,
    setupId,
    listItemId,
  }: {
    projectId: string;
    setupId: string;
    listItemId: string;
  }) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/staffing-setup/${setupId}/list/${listItemId}`,
    });
  };

type TBatchOperation =
  | { type: "updateListItem"; setupId: string; listItemId: string; changes: object }
  | { type: "updateSubcontractor"; subcontractorId: string; changes: object }
  | {
      type: "updateStaffingPeriod";
      setupId: string;
      listItemId: string;
      staffingPeriod: Array<{ startDate: Date; endDate: Date; days: number }>;
    };

const batchUpdateBudget =
  (
    axios: IFactoryFunctionRequest<
      {
        success: boolean;
        summary: { total: number; success: number; failed: number };
        results: Array<{ success: boolean; operation: TBatchOperation; error?: string }>;
      },
      any
    >,
  ) =>
  ({ projectId, operations }: { projectId: string; operations: TBatchOperation[] }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/budget/batch-update`,
      data: { operations },
      beforeSendingHook: (data) => {
        return {
          operations: data.operations.map((item) => {
            return beforeSendingList(item);
          }),
        };
      },
    });
  };

const deleteProject =
  (axios: IFactoryFunctionRequest<any, any>) =>
  ({ projectId }: { projectId: string }) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/deleteProject/${projectId}`,
    });
  };

const globalSearch =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (searchString = "") => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/search`,
      query: { searchString },
      immediate: false,
    });
  };

const createNote =
  (
    axios: IFactoryFunctionRequest<
      { headline: string; content: string; authorId: string },
      undefined
    >,
  ) =>
  (projectId, data) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/createNote`,
      data,
    });
  };

const editNote = (axios: IFactoryFunctionRequest<any, any>) => (projectId, noteId, data) => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/editNote/${noteId}`,
    data,
  });
};

const exportStaffingToExcel =
  (axios: IFactoryFunctionRequest<{ base64: string }, any>) =>
  ({ startDate, endDate, resources }: { startDate: Date; endDate: Date; resources: string[] }) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/export/staffing/${startDate.toISOString()}/${endDate.toISOString()}`,
      data: { resources },
    });
  };

const getMyStaffing =
  (axios: IFactoryFunctionRequest<any, any>) => (query: { page: number; limit: number }) => {
    return axios({
      method: EMethod.get,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/my-staffing`,
      query,
      afterReceivingHook: afterReceiving,
    });
  };

const bulkCreateTemplate = (axios: IFactoryFunctionRequest<any, any>) => (firmId?: string) => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/template`,
    query: firmId ? { firmId } : undefined,
  });
};

const bulkCreateValidate =
  (axios: IFactoryFunctionRequest<any, any>) => (file: File, firmId?: string) => {
    const formData = new FormData();
    formData.append("file", file);
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create/validate`,
      query: firmId ? { firmId } : undefined,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

const bulkCreate =
  (axios: IFactoryFunctionRequest<any, any>) =>
  (rows: { data: Record<string, any>; resolvedData?: Record<string, any> }[], firmId?: string, integrationExclusions?: string[]) => {
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/bulk-create`,
      data: {
        rows,
        ...(firmId ? { firmId } : {}),
        ...(integrationExclusions ? { integrationExclusions } : {}),
      },
    });
  };

const addDocuments =
  (axios: IFactoryFunctionRequest<{ documents: any[] }, FormData>) =>
  (projectId: string, files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });
    return axios({
      method: EMethod.post,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/documents`,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  };

const deleteDocument =
  (axios: IFactoryFunctionRequest<Record<string, never>, undefined>) =>
  (projectId: string, documentId: string) => {
    return axios({
      method: EMethod.delete,
      url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${projectId}/documents/${documentId}`,
    });
  };

export default (axios: AxiosInstance) => ({
  showFirmPaginated: showFirmPaginated(useAxios(axios)),
  showFunnelFirmPaginated: showFunnelFirmPaginated(useAxios(axios)),
  patchLeadStatus: patchLeadStatus(useAxios(axios)),
  createFunnelDeal: createFunnelDeal(useAxios(axios)),
  bulkLeadStatus: bulkLeadStatus(useAxios(axios)),
  getFunnelInsights: getFunnelInsights(useAxios(axios)),
  getDealWorkspace: getDealWorkspace(useAxios(axios)),
  patchFunnelCrm: patchFunnelCrm(useAxios(axios)),
  updateOneStaffing: updateOneStaffing(useAxios(axios)),
  updateOrCreateSetup: updateOrCreateSetup(useAxios(axios)),
  updateResourceStaffingList: updateResourceStaffingList(useAxios(axios)),
  updateResourceStaffingPosition: updateResourceStaffingPosition(useAxios(axios)),
  deleteResourceStaffingList: deleteResourceStaffingList(useAxios(axios)),
  updateProjectBasic: updateProjectBasic(useAxios(axios)),
  updateStreams: updateStreams(useAxios(axios)),
  getResourceStaffingBetweenDates: getResourceStaffingBetweenDates(useAxios(axios)),
  oldFilter: oldFilter(useAxios(axios)),
  getProjectInvoice: getProjectInvoice(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  filter: filter(useAxios(axios)),
  getOneStaffing: getOneStaffing(useAxios(axios)),
  getInterval: getInterval(useAxios(axios)),
  getProjectTimesheet: getProjectTimesheet(useAxios(axios)),
  getProjectBudget: getProjectBudget(useAxios(axios)),
  createProject: createProject(useAxios(axios)),
  showProjectTimesheet: showProjectTimesheet(useAxios(axios)),
  getAvailableTimesheetBooking: getAvailableTimesheetBooking(useAxios(axios)),
  checkIfProjectExist: checkIfProjectExist(useAxios(axios)),
  getClientProjects: getClientProjects(useAxios(axios)),
  showEngagementsList: showEngagementsList(useAxios(axios)),
  getProjectsToBeInvoiced: getProjectsToBeInvoiced(useAxios(axios)),
  getEvaluationProjectCreate: getEvaluationProjectCreate(useAxios(axios)),
  showAdvancement: showAdvancement(useAxios(axios)),
  archiveProjects: archiveProjects(useAxios(axios)),
  getProjectInformation: getProjectInformation(useAxios(axios)),
  showResourceConfirmed: showResourceConfirmed(useAxios(axios)),
  getIntervalPlanningResource: getIntervalPlanningResource(useAxios(axios)),
  getProjectCumulated: getProjectCumulated(useAxios(axios)),
  getProjectMarginMonitoring: getProjectMarginMonitoring(useAxios(axios)),
  // extractExpenseInformation: extractExpenseInformation(useAxios(axios)),
  availableForExpense: availableForExpense(useAxios(axios)),
  evaluationIntervalForResource: evaluationIntervalForResource(useAxios(axios)),
  exportProjectsToExcel: exportProjectsToExcel(useAxios(axios)),
  retreiveLastProjectCode: retreiveLastProjectCode(useAxios(axios)),
  addSubcontractor: addSubcontractor(useAxios(axios)),
  removeSubcontractor: removeSubcontractor(useAxios(axios)),
  updateSubcontractor: updateSubcontractor(useAxios(axios)),
  globalSearch: globalSearch(useAxios(axios)),
  duplicateProject: duplicateProject(useAxios(axios)),
  createNote: createNote(useAxios(axios)),
  editNote: editNote(useAxios(axios)),
  deleteProject: deleteProject(useAxios(axios)),
  // Atomic budget updates
  updateListItem: updateListItem(useAxios(axios)),
  updateStaffingPeriod: updateStaffingPeriod(useAxios(axios)),
  updateSubcontractorAtomic: updateSubcontractorAtomic(useAxios(axios)),
  deleteListItem: deleteListItem(useAxios(axios)),
  batchUpdateBudget: batchUpdateBudget(useAxios(axios)),
  exportStaffingToExcel: exportStaffingToExcel(useAxios(axios)),
  getMyStaffing: getMyStaffing(useAxios(axios)),
  bulkCreateTemplate: bulkCreateTemplate(useAxios(axios)),
  bulkCreateValidate: bulkCreateValidate(useAxios(axios)),
  bulkCreate: bulkCreate(useAxios(axios)),
  addDocuments: addDocuments(useAxios(axios)),
  deleteDocument: deleteDocument(useAxios(axios)),
});
